import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core'
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormControlStatus,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validators,
} from '@angular/forms'
import { Observable, Subject, Subscription } from 'rxjs'
import { debounceTime, map, startWith } from 'rxjs/operators'
import { ElementParse } from '../../ParseClass/Element.Parse'
import { ElementTypeCategoryEnum } from '../../ParseClass/ElementType.Parse'
import {
    calendarsModalityOperators,
    functionalityModalityOperators,
    opPossibleValues,
    SensorEventStatement,
    SensorEventStatementFunctionality_StatusType,
    SensorEventStatementModalityType,
    SensorEventStatementType,
    SpecialInfoName,
    StatementConfigOp,
} from '../../ParseClass/SensorEvent.Parse'
import {
    arrayIsSet,
    getOnlyValuesEnum,
    isNotNullOrUndefined, objectIsSet,
    stringIsSet,
} from '../../providers/service/global-variables.service'
import { IconService } from '../../providers/service/icon.service'
import { dataForm, GroupDataForm } from '../auto-complete-form/auto-complete-form.component'
import { PossibleValuesFormField } from '../multi-select-form-field/multi-select-form-field.component'
import { CalendarEvent } from "../../ParseClass/calendar-event";

@Component({
    selector: 'app-field-statement-sensor-event',
    templateUrl: './field-statement-sensor-event.component.html',
    styleUrls: ['./field-statement-sensor-event.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FieldStatementSensorEventComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FieldStatementSensorEventComponent,
        },
    ],
    standalone: false
})
export class FieldStatementSensorEventComponent
    implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() elements: ElementParse[] | undefined
    @Input() calendars: CalendarEvent[] | undefined
    @Input() sensorDataType: dataForm[] | undefined
    @Output() statusChange = new Subject<FormControlStatus>()

    public element$: Observable<ElementParse | undefined>
    public configElement$: Observable<ElementParse | undefined>
    public type$: Observable<SensorEventStatementType | undefined>
    opPossibleValues = opPossibleValues
    functionalityModalityOperators = functionalityModalityOperators
    calendarsModalityOperators = calendarsModalityOperators
    SensorEventStatementType = SensorEventStatementType
    SensorEventStatementFunctionality_StatusType =
        SensorEventStatementFunctionality_StatusType
    SensorEventStatementModalityType = SensorEventStatementModalityType
    StatementConfigOp = StatementConfigOp
    private subscriptions = new Subscription()
    public form: FormGroup
    infoName$: Observable<string>
    SpecialInfoName = SpecialInfoName
    public daysOfWeek = [
        { id: 0, name: 'Sunday' },
        { id: 1, name: 'Monday' },
        { id: 2, name: 'Tuesday' },
        { id: 3, name: 'Wednesday' },
        { id: 4, name: 'Thursday' },
        { id: 5, name: 'Friday' },
        { id: 6, name: 'Saturday' },
    ]
    selectCalendarId$: Observable<string | undefined>
    selectCalendar$: Observable<CalendarEvent | undefined>

    constructor(
        fb: FormBuilder, iconService: IconService,
    ) {
        iconService.addSvgIcon('=', 'assets/icon/math/equals.svg')
        iconService.addSvgIcon('!=', 'assets/icon/math/notEquals.svg')
        iconService.addSvgIcon('>', 'assets/icon/math/greather.svg')
        iconService.addSvgIcon('>=', 'assets/icon/math/greatherOrEquals.svg')
        iconService.addSvgIcon('<', 'assets/icon/math/less.svg')
        iconService.addSvgIcon('<=', 'assets/icon/math/lessOrEquals.svg')
        iconService.addSvgIcon('d!=', 'assets/icon/math/d!=.svg')
        iconService.addSvgIcon('t!=', 'assets/icon/math/t!=.svg')
        iconService.addSvgIcon('det!=', 'assets/icon/math/det!=.svg')
        iconService.addSvgIcon('dot!=', 'assets/icon/math/dot!=.svg')
        this.form = fb.group({
            infoName: [undefined, [Validators.required]],
            type: [SensorEventStatementType.FIXED, [Validators.required]],
            sensorId: [undefined, [Validators.required]],
            sensorIds: [undefined],
            op: [undefined, [Validators.required]],
            deltaRange: [undefined],
            configTypeFixed: [undefined, [Validators.required]],
            configTypeSensor: fb.group({
                sensorId: [undefined, [Validators.required]],
                infoName: [undefined, [Validators.required]],
            }),
            configTypeProgram: fb.group({
                days: [[], [Validators.required]],
                start_hours: [0, [Validators.required, Validators.min(0), Validators.max(23)]],
                start_minutes: [0, [Validators.required, Validators.min(0), Validators.max(59)]],
            }),
            configTypeAggregate: fb.group({
                value: [undefined, [Validators.required]],
                op: [undefined, [Validators.required]],
            }),
            configTypeCalendar: fb.group({
                value: [undefined, [Validators.required]],
                op: ['=', [Validators.required]],
            }),
            skipIfNull: [false],
        })
        this.selectCalendarId$ = (this.form.get('configTypeCalendar') as any).get('value')?.valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const calendar = (this.form.get('configTypeCalendar') as any).get('value')?.value
                if (calendar) {
                    return calendar.calendarId
                } else {
                    return;
                }
            })
        )
        this.selectCalendar$ = this.selectCalendarId$.pipe(
            map(calendarId => {
                return this.calendars?.find(c => c.objectId == calendarId)
            })
        )
        this.infoName$ = (this.form.get('infoName') as any).valueChanges.pipe(
            startWith(undefined)
        )
        this.element$ = (this.form.get('sensorId') as any).valueChanges.pipe(
            startWith(undefined),
            debounceTime(200),
            map(() => {
                const sensorId = this.form.get('sensorId')?.value
                if (arrayIsSet(this.elements) && stringIsSet(sensorId)) {
                    return this.elements.find(d => d.objectId === sensorId)
                } else {
                    return undefined
                }
            })
        )
        this.configElement$ = (this.form.get('configTypeSensor') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const config = this.form.get('configTypeSensor')?.value
                const sensorId =
                    config != null && 'sensorId' in config ? config.sensorId : undefined
                if (arrayIsSet(this.elements) && stringIsSet(sensorId)) {
                    return this.elements.find(d => d.objectId === sensorId)
                } else {
                    return undefined
                }
            })
        )
        this.type$ = (this.form.get('type') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => this.form.get('type')?.value)
        )

        this.subscriptions.add(
            this.type$.subscribe(type => {
                if (
                    type == SensorEventStatementType.FUNCTIONALITY_STATUS ||
                    type == SensorEventStatementType.ACTIVITY_STATUS
                ) {
                    this.form.get('sensorId')?.removeValidators([Validators.required])
                    this.form.get('infoName')?.removeValidators([Validators.required])
                    this.form.get('configTypeFixed')?.enable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type === SensorEventStatementType.SCENARIO) {
                    this.form.get('sensorId')?.addValidators([Validators.required])
                    this.form.get('infoName')?.addValidators([Validators.required])
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeSensor')?.get('sensorId')!.enable()
                    this.form.get('configTypeSensor')?.get('infoName')!.disable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type === SensorEventStatementType.SENSOR) {
                    this.form.get('sensorId')?.addValidators([Validators.required])
                    this.form.get('infoName')?.addValidators([Validators.required])
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeSensor')?.enable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type === SensorEventStatementType.FIXED) {
                    this.form.get('sensorId')?.addValidators([Validators.required])
                    this.form.get('infoName')?.addValidators([Validators.required])
                    this.form.get('configTypeFixed')?.enable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type === SensorEventStatementType.PROGRAM) {
                    this.form.get('sensorId')?.removeValidators([Validators.required])
                    this.form.get('infoName')?.removeValidators([Validators.required])
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeProgram')?.enable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type === SensorEventStatementType.AGGREGATE) {
                    this.form.get('sensorId')?.removeValidators([Validators.required])
                    this.form.get('infoName')?.removeValidators([Validators.required])
                    this.form.get('sensorIds')?.addValidators([Validators.required])
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.enable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type === SensorEventStatementType.CALENDAR) {
                    this.form.get('sensorId')?.removeValidators([Validators.required])
                    this.form.get('infoName')?.removeValidators([Validators.required])
                    this.form.get('sensorIds')?.removeValidators([Validators.required])
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.enable()
                    this.form.get('op')?.setValue('=')
                    Object.keys(this.form.value).forEach(key => {
                        if (this.form.get(key)?.invalid) {
                            this.form.get(key)?.updateValueAndValidity()
                        }
                    })
                } else {
                    this.form.get('sensorId')?.addValidators([Validators.required])
                    this.form.get('infoName')?.addValidators([Validators.required])
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeCalendar')?.disable()
                }


            })
        )
        this.subscriptions.add(
            this.infoName$.subscribe(type => {
                if (type == SpecialInfoName.deviceStatus) {
                    this.form.get('deltaRange')?.disable()
                    this.form.get('type')?.setValue(SensorEventStatementType.FIXED)
                    this.form.get('type')?.disable()
                    // this.form.get('op')?.reset();
                    // this.form.get('configTypeFixed')?.reset();
                } else {
                    this.form.get('deltaRange')?.enable()
                    this.form.get('type')?.enable()
                }
            })
        )
        this.subscriptions.add(
            this.form.statusChanges
                .pipe(
                    startWith(undefined),
                    map(() => {
                        return this.form.status
                    })
                )
                .subscribe(status => {
                    setTimeout(() => {
                        this.statusChange.next(status)
                    })
                })
        )
    }

    ngOnInit(): void {
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe()
    }

    onTouched: Function = () => {
    }

    registerOnChange(onChange: any) {
        const values$ = this.form.valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const values = this.form.value
                if (values != null) {
                    const rawValues = this.form.getRawValue()
                    const obj = { ...values }
                    const type = rawValues.type
                    let config
                    if (
                        type === SensorEventStatementType.FIXED ||
                        type == SensorEventStatementType.FUNCTIONALITY_STATUS ||
                        type == SensorEventStatementType.ACTIVITY_STATUS
                    ) {
                        config = rawValues.configTypeFixed
                    } else if (
                        type === SensorEventStatementType.SENSOR ||
                        type === SensorEventStatementType.SCENARIO
                    ) {
                        config = values.configTypeSensor
                    } else if (type === SensorEventStatementType.PROGRAM) {
                        config = {
                            days: rawValues.configTypeProgram.days,
                            start_hours: rawValues.configTypeProgram.start_hours,
                            start_minutes: rawValues.configTypeProgram.start_minutes,
                        }
                    } else if (type === SensorEventStatementType.AGGREGATE) {
                        config = {
                            op: rawValues.configTypeAggregate.op,
                            value: rawValues.configTypeAggregate.value,
                        }
                    } else if (type === SensorEventStatementType.CALENDAR) {
                        config = rawValues.configTypeCalendar.value

                    }
                    delete obj.configTypeSensor
                    delete obj.configTypeFixed
                    delete obj.configTypeProgram
                    delete obj.configTypeAggregate
                    delete obj.configTypeCalendar
                    obj['config'] = config
                    if (obj.type == null) {
                        obj['type'] = type
                    }
                    return obj
                } else {
                    return
                }
            })
        )
        this.subscriptions.add(values$.subscribe(onChange))
    }

    setDisabledState(disabled: boolean) {
        if (disabled) {
            this.form.disable()
        } else {
            this.form.enable()
            const type = this.form.get('type')?.value
            this.form.get('type')?.setValue(type)
        }
    }

    writeValue(value: SensorEventStatement) {
        if (isNotNullOrUndefined(value)) {
            const type = value.type ?? SensorEventStatementType.FIXED
            const valuesEnum = getOnlyValuesEnum(SensorEventStatementType)
            if (valuesEnum.includes(type.toString())) {
                value.type = type
            } else {
                value.type = SensorEventStatementType.SENSOR
            }
            const specialKeys = ['config']
            Object.keys(value)
                .filter(key => !specialKeys.includes(key))
                .forEach(key => {
                    if (this.form.get(key) != null) {
                        this.form.get(key)?.setValue((value as any)[key])
                    }
                })

            const config: any = value.config
            if (config != null) {
                if (type == SensorEventStatementType.FIXED) {
                    this.form.get('configTypeFixed')?.setValue(config)
                    this.form.get('configTypeFixed')?.enable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type == SensorEventStatementType.SENSOR) {
                    const sensorId = config?.sensorId
                    const infoName = config?.infoName
                    this.form.get('configTypeSensor')?.get('sensorId')?.setValue(sensorId)
                    this.form.get('configTypeSensor')?.get('infoName')?.setValue(infoName)
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeSensor')?.enable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type == SensorEventStatementType.SCENARIO) {
                    const sensorId = config?.sensorId
                    this.form.get('configTypeSensor')?.get('sensorId')?.setValue(sensorId)
                    this.form.get('configTypeSensor')?.get('sensorId')?.enable()
                    this.form.get('configTypeSensor')?.get('infoName')?.disable()
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (
                    type === SensorEventStatementType.FUNCTIONALITY_STATUS ||
                    type === SensorEventStatementType.ACTIVITY_STATUS
                ) {
                    this.form.get('configTypeFixed')?.setValue(config)
                    this.form.get('configTypeFixed')?.enable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type === SensorEventStatementType.PROGRAM) {
                    const days = config?.days
                    const start_hours = config?.start_hours
                    const start_minutes = config?.start_minutes
                    this.form.get('configTypeProgram')?.get('days')?.setValue(days)
                    this.form.get('configTypeProgram')?.get('start_hours')?.setValue(start_hours)
                    this.form
                        .get('configTypeProgram')
                        ?.get('start_minutes')
                        ?.setValue(start_minutes)
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeProgram')?.enable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type === SensorEventStatementType.AGGREGATE) {
                    const configValue = config?.value
                    const configOp = config?.op
                    this.form.get('configTypeAggregate')?.get('value')?.setValue(configValue)
                    this.form.get('configTypeAggregate')?.get('op')?.setValue(configOp)
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.enable()
                    this.form.get('configTypeCalendar')?.disable()
                } else if (type === SensorEventStatementType.CALENDAR) {
                    const configValue = config?.calendarId
                    let configOp = value?.op
                    this.form.get('configTypeFixed')?.disable()
                    this.form.get('configTypeSensor')?.disable()
                    this.form.get('configTypeProgram')?.disable()
                    this.form.get('configTypeAggregate')?.disable()
                    this.form.get('configTypeCalendar')?.enable()
                    this.form.get('configTypeCalendar')?.get('value')?.setValue({ calendarId: configValue })
                    if (configOp == null) {
                        configOp = value.op
                    }
                    if (configOp == null) {
                        configOp = '='
                    }
                    this.form.get('op')?.setValue(configOp)
                    this.form.get('configTypeCalendar')?.get('op')?.setValue(configOp)

                    setTimeout(() => {
                        this.form.get('op')?.setValue(configOp)
                        this.form.get('configTypeCalendar')?.get('op')?.setValue(configOp)
                        console.log(this.form.get('op')?.value,
                            this.form.get('configTypeCalendar')?.get('op')?.value)
                    }, 200)
                }
                setTimeout(() => {
                    this.form.get('type')?.setValue(type)
                }, 20)
            }
        } else if (value) {
            this.form.reset()
        }
    }


    validate(control: AbstractControl): ValidationErrors | null {
        // if (!isNotNullOrUndefined(this.form.get('autoComplete')?.validator)) {
        //     this.form.get('autoComplete')?.setValidators(control.validator)
        //     this.form.get('autoComplete')?.updateValueAndValidity()
        // }
        if (this.form.valid) {
            return null
        } else {
            return this.form.errors!
        }
    }

    addControlErrors(allErrors: any, controlName: string) {
        const errors = { ...allErrors }
        const controlErrors = this.form.controls[controlName].errors
        if (controlErrors) {
            errors[controlName] = controlErrors
        }
        return errors
    }

    registerOnTouched(onTouched: Function) {
        this.onTouched = onTouched
    }

    predicatePossibleValuesElements(elements: ElementParse[]): dataForm[] | undefined {
        if (arrayIsSet(elements)) {
            return elements
                .filter(
                    el =>
                        el.type != null &&
                        el.type.periodicData != null &&
                        arrayIsSet(Object.keys(el.type.periodicData))
                )
                .map(device => {
                    const typeName =
                        device.type != null && stringIsSet(device.type.name)
                            ? device.type.name
                            : undefined
                    const pValue: dataForm = {
                        valueForm: device.objectId!,
                        html: device.objectId!,
                        traduction: stringIsSet(typeName)
                            ? device.name + ' - ' + typeName
                            : device.name,
                    }
                    return pValue
                })
        } else {
            return undefined
        }
    }

    predicatePossibleValuesElementsRaggruped(elements: ElementParse[], suggestedTrad: string, oherTrad: string): GroupDataForm[] | undefined {
        if (arrayIsSet(elements)) {

            const possibleValuesSuggested = elements
                .filter(
                    el =>
                        el.type != null &&
                        el.type.periodicData != null &&
                        arrayIsSet(Object.keys(el.type.periodicData))
                )
                .map(device => {
                    const typeName =
                        device.type != null && stringIsSet(device.type.name)
                            ? device.type.name
                            : undefined
                    const pValue: dataForm = {
                        valueForm: device.objectId!,
                        html: device.objectId!,
                        traduction: stringIsSet(typeName)
                            ? device.name + ' - ' + typeName
                            : device.name,
                    }
                    return pValue
                })
            const possibleValuesOther = elements
                .filter(
                    el =>
                        el.type == null ||
                        el.type.periodicData == null ||
                        !arrayIsSet(Object.keys(el.type.periodicData))
                )
                .map(device => {
                    const typeName =
                        device.type != null && stringIsSet(device.type.name)
                            ? device.type.name
                            : undefined
                    const pValue: dataForm = {
                        valueForm: device.objectId!,
                        html: device.objectId!,
                        traduction: stringIsSet(typeName)
                            ? device.name + ' - ' + typeName
                            : device.name,
                    }
                    return pValue
                })
            const group: GroupDataForm[] = [
                {
                    possibleValues: possibleValuesSuggested,
                    groupName: suggestedTrad
                }, {
                    possibleValues: possibleValuesOther,
                    groupName: oherTrad
                }
            ]
            return group
        } else {
            return undefined
        }
    }


    predicatePossibleValuesInfoNameGrouped(element: ElementParse, sensorDataType: dataForm[], suggestedTrad: string, otherTrad: string): GroupDataForm[] | undefined {
        if (element) {
            const groups: GroupDataForm[] = []
            const keysPeriodicData: string[] = []
            if (element.type && objectIsSet(element.type.periodicData)) {
                const keysPeriodicData = Object.keys(element.type.periodicData)
                groups.push({
                    groupName: suggestedTrad,
                    possibleValues: keysPeriodicData.map(key => {
                        const obj: dataForm = {
                            valueForm: key,
                            html: key,
                            sensorEventTraduction: key,
                        }
                        return obj
                    })
                });
            }
            groups.push({
                groupName: otherTrad,
                possibleValues: sensorDataType!.filter(sensorData => !keysPeriodicData.includes(sensorData.valueForm!))
            })
            return groups
        } else {
            return undefined
        }
    }

    predicatePossibleValuesInfoName(
        device: ElementParse | undefined,
        specialInfoName: any
    ): dataForm[] | undefined {
        if (device != null) {
            const elementType = device.type
            if (elementType?.periodicData != null) {
                const periodicData: any = elementType.periodicData
                let possibleValues: dataForm[] = []
                if (specialInfoName && arrayIsSet(Object.keys(specialInfoName))) {
                    Object.keys(specialInfoName).forEach(key => {
                        const pValue: dataForm = {
                            valueForm: specialInfoName[key],
                            html: specialInfoName[key],
                            traduction: key,
                        }
                        possibleValues.push(pValue)
                    })
                }
                if (elementType.category === ElementTypeCategoryEnum.VIRTUAL_SENSOR) {
                    if (arrayIsSet(Object.keys(elementType.periodicData))) {
                        possibleValues = Object.keys(elementType.periodicData).map(key => {
                            const pValue: dataForm = {
                                valueForm: key,
                                html: key,
                                sensorEventTraduction: key,
                            }
                            return pValue
                        })
                    }
                }

                possibleValues = possibleValues.concat(
                    Object.keys(periodicData).map(key => {
                        const pValue: dataForm = {
                            valueForm: key,
                            html: key,
                            sensorEventTraduction: key,
                        }
                        return pValue
                    })
                )

                return possibleValues
            }
            return undefined
        } else {
            return undefined
        }
    }

    predicatePossibleValueType(sensorEventStatementType: SensorEventStatementType): {
        traduction: string
        valueForm: any
    }[] {
        let keys = Object.keys(sensorEventStatementType).filter(x => !(parseInt(x) >= 0))
        return keys.map(key => {
            return {
                traduction:
                    'SensorEvent.SensorEventStatementType.' +
                    (sensorEventStatementType as any)[key],
                valueForm: (sensorEventStatementType as any)[key],
            }
        })
    }

    predicateSensorEventStatementFunctionality_StatusType(
        sensorEventStatementType: SensorEventStatementFunctionality_StatusType
    ): {
        traduction: string
        valueForm: any
    }[] {
        let keys = Object.keys(sensorEventStatementType).filter(x => !(parseInt(x) >= 0))
        return keys.map(key => {
            return {
                traduction:
                    'SensorEvent.SensorEventStatementFunctionality_StatusType.' +
                    (sensorEventStatementType as any)[key],
                valueForm: (sensorEventStatementType as any)[key],
            }
        })
    }

    predicateSensorEventStatementActivity_StatusType(
        sensorEventStatementType: SensorEventStatementModalityType
    ): {
        traduction: string
        valueForm: any
    }[] {
        let keys = Object.keys(sensorEventStatementType).filter(x => !(parseInt(x) >= 0))
        return keys.map(key => {
            return {
                traduction:
                    'SensorEvent.SensorEventStatementModalityType.' +
                    (sensorEventStatementType as any)[key],
                valueForm: (sensorEventStatementType as any)[key],
            }
        })
    }

    predicateSensorEventStatementActivity_Operator(operator: StatementConfigOp): {
        traduction: string
        valueForm: any
    }[] {
        let keys = Object.keys(operator).filter(x => !(parseInt(x) >= 0))
        return keys.map(key => {
            return {
                traduction: 'SensorEvent.StatementConfigOp.' + (operator as any)[key],
                valueForm: (operator as any)[key],
            }
        })
    }

    predicateOperator(operators: string[], infoName: string, specialInfoName: any) {
        if (infoName === specialInfoName.deviceStatus) {
            return ['=']
        } else {
            return operators
        }
    }

    toggleAllSensors(selectAll: boolean): void {
        const newIds = selectAll && this.elements ? this.elements.map(el => el.objectId) : []
        this.form.get('sensorIds')?.setValue(newIds)
        this.form.get('sensorIds')?.markAsDirty()
    }

    predicatePossibleValuesForMultiSelect(
        elements: ElementParse[]
    ): PossibleValuesFormField[] {
        if (!arrayIsSet(elements)) return []

        return elements.map(el => {
            const typeName = el.type?.name
            const deviceName = el.name

            return {
                objectId: el.objectId,
                registerValue: el.objectId,
                traduction: typeName ? `${deviceName} - ${typeName}` : deviceName,
                disabled: false,
            } as PossibleValuesFormField
        })
    }

    setCalendar(calendar: CalendarEvent) {
        this.form.get('configTypeCalendar')?.get('value')?.setValue({ calendarId: calendar.objectId })
    }

    getSelectColor(calendar: CalendarEvent, selectCalendarId: string | undefined) {
        if (selectCalendarId && calendar && selectCalendarId == calendar.objectId) {
            return 'background-color-primary'
        } else {
            return ''
        }
    }
}
